// Studio theme: the only colour a studio picks is its primary colour. The
// rest of the public site (and later the widget) is a fixed warm base, so
// every accent token is derived here, with AA contrast guaranteed whatever
// colour the studio chose.

export type StudioTheme = {
  /** Button and selection fill. Same as the studio colour unless no text colour reaches AA on it. */
  accent: string;
  /** ~90% tint of the studio colour: chips, avatars, selected rows. */
  accentSoft: string;
  /** Text on `accent`: white or ink, whichever contrasts more. */
  onAccent: string;
  /** Accent used as text or icon on white and on `accentSoft` (links, chips). */
  accentInk: string;
};

export const DEFAULT_STUDIO_COLOR = "#c07a5c";

const WHITE = "#ffffff";
const BLACK = "#000000";
const INK = "#22302b";
const AA = 4.5;

type Rgb = [number, number, number];

export function normalizeHex(color: string | null | undefined): string | null {
  if (!color) return null;
  const hex = color.trim().replace(/^#/, "");
  if (/^[0-9a-f]{3}$/i.test(hex)) {
    return `#${hex
      .split("")
      .map((c) => c + c)
      .join("")}`.toLowerCase();
  }
  if (/^[0-9a-f]{6}$/i.test(hex)) return `#${hex.toLowerCase()}`;
  return null;
}

function toRgb(hex: string): Rgb {
  const v = hex.slice(1);
  return [0, 2, 4].map((i) => parseInt(v.slice(i, i + 2), 16)) as Rgb;
}

function toHex(rgb: Rgb): string {
  return `#${rgb.map((c) => Math.round(c).toString(16).padStart(2, "0")).join("")}`;
}

/** Linear mix: `t` = 0 gives `from`, 1 gives `to`. */
export function mixHex(from: string, to: string, t: number): string {
  const a = toRgb(from);
  const b = toRgb(to);
  return toHex(a.map((c, i) => c + (b[i]! - c) * t) as Rgb);
}

function relativeLuminance(hex: string): number {
  const [r, g, b] = toRgb(hex).map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  }) as Rgb;
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/** Darkens `color` towards black until it reaches AA against every background. */
function darkenUntilReadable(color: string, backgrounds: string[]): string {
  for (let t = 0; t <= 1; t += 0.05) {
    const candidate = mixHex(color, BLACK, t);
    if (backgrounds.every((bg) => contrastRatio(candidate, bg) >= AA)) {
      return candidate;
    }
  }
  return BLACK;
}

export function deriveStudioTheme(color: string | null | undefined): StudioTheme {
  const base = normalizeHex(color) ?? DEFAULT_STUDIO_COLOR;
  const accentSoft = mixHex(base, WHITE, 0.9);

  // Mid-tone colours (luminance around 0.23) reach at most ~3.7:1 with both
  // white and ink. Only those get darkened, until white text passes.
  const best = contrastRatio(WHITE, base) >= contrastRatio(INK, base) ? WHITE : INK;
  const accent =
    contrastRatio(best, base) >= AA ? base : darkenUntilReadable(base, [WHITE]);
  const onAccent = accent === base ? best : WHITE;

  return {
    accent,
    accentSoft,
    onAccent,
    accentInk: darkenUntilReadable(base, [WHITE, accentSoft]),
  };
}

/** CSS variables read by the `studio-*` Tailwind utilities (see theme.css). */
export function studioThemeVars(theme: StudioTheme): Record<string, string> {
  return {
    "--studio-accent": theme.accent,
    "--studio-accent-soft": theme.accentSoft,
    "--studio-on-accent": theme.onAccent,
    "--studio-accent-ink": theme.accentInk,
  };
}

/** "Studio Lac" → "SL", "Ananda" → "AN". */
export function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "";
  if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase();
  return (words[0]![0]! + words[1]![0]!).toUpperCase();
}
